import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { useExportPaymentsMutation, useGetPaymentsQuery, useUpdatePaymentMutation } from 'api/main';
import { userSelectors } from 'reducers';
import {
    listingActionButtonStyle,
    ORDER_BY,
    paymentEnrichments,
    paymentMethods,
    paymentStatus,
    roleCategories,
    RoleNames,
    sortDirection,
} from 'utils/constants';
import { useDebounce, useMatchAtLeastOneRole, useUserRoleMatchCategory } from 'utils/hooks';
import {
    getPartialPaymentsTotal,
    jsonParseSafe,
    parseFullName,
    parsePaymentNumber,
    parseSum,
} from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionsMenu,
    CustomTablePagination,
    EmptyTableBody,
    SMSDialog,
    StyledTableCell,
    StyledTableRow,
    Loader,
    QuickSearchField,
    CustomDialogTitle,
    CancelButton,
    ActionButton,
    CustomDatePicker,
    FiltersButton,
} from 'components';
import { MenuSendMessage, MenuSendSMS } from 'components/ActionsMenu/actions';
import { SettleMenuItem } from './DebitsTable';
import CreditDialog from './CreditDialog';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    TableFooter,
    MenuItem,
    ListItemText,
    Dialog,
    DialogActions,
    DialogContent,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as icons from 'mdi-material-ui';
import PaymentFilters from './PaymentFilters';

const SendMessageItem = React.forwardRef((pr, ref) => <MenuSendMessage ref={ref} {...pr} />);
const SettleItem = React.forwardRef((pr, ref) => <SettleMenuItem ref={ref} {...pr} />);
const CreditItem = React.forwardRef((pr, ref) => <CreditMenuItem ref={ref} {...pr} />);

const useStyles = makeStyles(theme => ({
    redRow: {
        color: theme.palette.error.main,
    },
    orangeRow: {
        color: theme.palette.warning.main,
    },
    totalRow: {
        borderBottom: 'none',
    },
    partial: { margin: 0 },
    container: {
        backgroundColor: theme.palette.grey[100],
        marginTop: theme.spacing(4),
        border: `1px solid ${theme.palette.grey[100]}`,
        borderRadius: 6,
        padding: '10px 15px',
    },
    paper: { minWidth: 0 },
}));

const commonTableHeads = [
    { id: 1, isSortable: false, width: 11, label: texts.NOM },
    { id: 2, isSortable: false, width: 10, label: texts.DATE },
    { id: 3, isSortable: false, width: 8, label: texts.TYPE },
    { id: 4, isSortable: false, width: 11, label: texts.FUND },
    { id: 5, isSortable: false, width: 7, label: texts.ORDER_NUMBER },
    { id: 6, isSortable: false, width: 12, label: texts.MONTANT_SOUSCRIT },
    { id: 7, isSortable: false, width: 8, label: texts.MONTANT_VIREMENT },
    { id: 8, isSortable: false, width: 8, label: texts.CREDITED_AMOUNT },
];

const transferTableHeads = [
    ...commonTableHeads,
    { id: 9, isSortable: false, width: 9, label: texts.SUM_REMAINING },
    { id: 10, isSortable: false, width: 12, label: texts.FIRST_TRANSFER_DATE },
    { id: 'actions', isSortable: false, width: 4, label: '' },
];

const historyTableHeads = [
    ...commonTableHeads,
    { id: 9, isSortable: false, width: 12, label: texts.TRANSFER_DATE },
    { id: 10, isSortable: false, width: 13, label: texts.SETTLED_BY },
];

const ITEMS_PER_PAGE = 10;

const CreditMenuItem = ({ classes, loading, ...rest }) => (
    <MenuItem disabled={loading} classes={{ root: classes.menuItem }} {...rest}>
        <ListItemText disableTypography classes={{ root: classes.itemText }}>
            <Loader loading={loading} marginTop={0}>
                <icons.CashPlus className={classes.actionIcons} /> {texts.CREDIT_PAYMENT}
            </Loader>
        </ListItemText>
    </MenuItem>
);

const TotalRow = ({ total, label, classes, hasActions }) => (
    <TableRow>
        <TableCell colSpan={6} align="right" classes={{ root: classes.totalRow }}>
            <strong>{label}</strong>
        </TableCell>
        <TableCell colSpan={hasActions ? 5 : 3} classes={{ root: classes.totalRow }}>
            {parseSum(total)}
        </TableCell>
    </TableRow>
);

const today = moment().startOf('day');

const TransferRow = props => {
    const { transfer, classes, hasActions, isServicer, onSendSMS, onCredit } = props;
    const {
        recipientId,
        recipientName,
        closingComment,
        0: {
            id,
            fluxType,
            investedAmount,
            amount,
            status,
            validatedBy,
            transferDate,
            fundraisingNumber,
            subscription: {
                finishedSubscriptionAt,
                investmentCharacteristics: {
                    shareSubcategory: { immediatePayment },
                },
                investmentFund: { name: fundName, type: fundType, firstInstalmentPercentage },
            },
        },
    } = transfer;
    const user = { id: recipientId, fullName: recipientName };
    const [ongoingAction, setOngoingAction] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [updatePayment, { isSuccess: updateSuccess }] = useUpdatePaymentMutation();
    const isConfirmed = useMemo(() => status === paymentStatus.CONFIRMED, [status]);
    const partialPayments = useMemo(
        () => jsonParseSafe(transfer[0].partialPayments, []),
        [transfer]
    );
    const partialsTotal = useMemo(() => getPartialPaymentsTotal(transfer[0]), [transfer]);
    const paymentTransferDate = transferDate ? moment.utc(transferDate) : null;
    const isUrgent = useMemo(
        () => today.isSameOrAfter(paymentTransferDate, 'day'),
        [paymentTransferDate]
    );
    const isCritical = useMemo(
        () =>
            paymentTransferDate
                ? today.isAfter(moment(paymentTransferDate).add(2, 'day'), 'day')
                : false,
        [paymentTransferDate]
    );
    const rowClass = useMemo(
        () => (isCritical ? classes.redRow : isUrgent ? classes.orangeRow : ''),
        [classes, isCritical, isUrgent]
    );
    const paymentNumberText =
        firstInstalmentPercentage === 100 && immediatePayment
            ? texts.FULL_CALL
            : parsePaymentNumber(
                  fluxType,
                  !!firstInstalmentPercentage ? fundraisingNumber + 1 : fundraisingNumber,
                  closingComment
              );
    const [settleDate, setSettleDate] = useState(paymentTransferDate);

    const handleDateChange = useCallback(newDate => setSettleDate(newDate), []);

    const handleUpdateStatus = useCallback(
        (paymentId, newStatus) => () => {
            setOngoingAction(newStatus);
            updatePayment({
                id: paymentId,
                status: newStatus,
                transferDate:
                    newStatus === paymentStatus.CONFIRMED ? settleDate.toISOString() : undefined,
            });
        },
        [updatePayment, settleDate]
    );

    useEffect(() => {
        if (updateSuccess) {
            setMenuOpen(false);
            setOngoingAction('');
        }
    }, [updateSuccess]);

    const handleCredit = useCallback(() => {
        onCredit(transfer[0]);
        setMenuOpen(false);
    }, [transfer, onCredit]);

    return (
        <StyledTableRow key={id} classes={{ root: hasActions ? rowClass : '' }}>
            <StyledTableCell>{recipientName}</StyledTableCell>
            <StyledTableCell>{moment(finishedSubscriptionAt).format('L')}</StyledTableCell>
            <StyledTableCell>{fundType}</StyledTableCell>
            <StyledTableCell>{fundName}</StyledTableCell>
            <StyledTableCell>{paymentNumberText}</StyledTableCell>
            <StyledTableCell>{parseSum(investedAmount)}</StyledTableCell>
            <StyledTableCell>{parseSum(amount)}</StyledTableCell>
            <StyledTableCell>
                {!partialPayments.length && parseSum(0)}
                {partialPayments.map((p, idx) => (
                    <p key={idx} className={classes.partial}>
                        {parseSum(p.amount)}
                    </p>
                ))}
            </StyledTableCell>
            {hasActions && <StyledTableCell>{parseSum(amount - partialsTotal)}</StyledTableCell>}
            <StyledTableCell>{paymentTransferDate.format('L')}</StyledTableCell>
            {hasActions && isServicer && (
                <StyledTableCell padding="checkbox" align="center">
                    <ActionsMenu
                        controlled
                        open={menuOpen}
                        handleOpen={(value = true) => setMenuOpen(value)}
                    >
                        <CreditItem onClick={handleCredit} />
                        <SettleItem
                            disabled={partialsTotal < amount && partialsTotal > 0}
                            onClick={() => setDialogOpen(true)}
                        />
                        <SendMessageItem receiver={user} />
                        <MenuSendSMS onSendSMS={onSendSMS(user)} id={id} />
                    </ActionsMenu>
                </StyledTableCell>
            )}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                classes={{ paper: classes.paper }}
            >
                <CustomDialogTitle title={texts.SETTLE_PAYMENT} />
                <DialogContent>
                    <CustomDatePicker
                        shouldDisableDate={day => day.day() === 0 || day.day() === 6}
                        onChange={handleDateChange}
                        value={settleDate}
                    />
                </DialogContent>
                <DialogActions>
                    <CancelButton
                        disabled={ongoingAction === paymentStatus.CONFIRMED}
                        onClick={() => setDialogOpen(false)}
                    >
                        {texts.CANCEL}
                    </CancelButton>
                    <ActionButton
                        disabled={ongoingAction === paymentStatus.CONFIRMED}
                        loading={ongoingAction === paymentStatus.CONFIRMED}
                        onClick={handleUpdateStatus(id, paymentStatus.CONFIRMED)}
                    >
                        {texts.SETTLE_PAYMENT}
                    </ActionButton>
                </DialogActions>
            </Dialog>
            {isConfirmed && (
                <StyledTableCell>{validatedBy ? parseFullName(validatedBy) : '-'}</StyledTableCell>
            )}
        </StyledTableRow>
    );
};

const TransfersTable = props => {
    // @hasActions prop also determines if the table needs styled rows or not
    const { title, hasActions, listStatus } = props;

    const classes = useStyles();
    const mounted = useRef(false);
    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const isServicer = useUserRoleMatchCategory(roleCategories.SERVICERS);
    const userIsStandardServicer = useMatchAtLeastOneRole([RoleNames.SERVICER_STANDARD]);

    const isAdminCommercial = useMatchAtLeastOneRole([RoleNames.ADMIN_CIF, RoleNames.PSI]);

    const [filters, setFilters] = useState({
        orderBy: ORDER_BY.finishedSubscriptionAt,
        direction: sortDirection.ASC,
        enrichments: Object.values(paymentEnrichments),
        type: paymentMethods.CREDIT_TRANSFER,
        company: isAdminCommercial ? currentSubscription?.distributorCompany?.id : undefined,
        status: listStatus,
        itemsPerPage: ITEMS_PER_PAGE,
        page: 1,
    });
    const [smsOpen, setSmsOpen] = useState(false);
    const [creditOpen, setCreditOpen] = useState(false);
    const [receiver, setReceiver] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(null);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, 500);

    const {
        data: { payments: transfers = [], lastPage } = { payments: [], lastPage: 1 },
        isFetching: listLoader,
        refetch,
    } = useGetPaymentsQuery(filters);
    const [exportPayments, { isLoading: exportLoading }] = useExportPaymentsMutation();

    const totalValue = useMemo(
        () => transfers.reduce((acc, crt) => acc + crt[0].amount, 0),
        [transfers]
    );

    const tableHeaders = useMemo(
        () => (hasActions ? transferTableHeads : historyTableHeads),
        [hasActions]
    );

    const filteredTableHeads = useMemo(() => {
        if (isServicer)
            return tableHeaders.filter(th => (userIsStandardServicer ? th.id !== 'actions' : true));
        if (hasActions) {
            return tableHeaders.filter(th => th.id !== 'actions');
        }
        return tableHeaders;
    }, [isServicer, tableHeaders, hasActions, userIsStandardServicer]);

    const leftOutWidth = useMemo(
        () =>
            tableHeaders
                .filter(th => !filteredTableHeads.find(fth => fth.id === th.id))
                .reduce((acc, crt) => acc + crt.width, 0),
        [tableHeaders, filteredTableHeads]
    );
    const handleToggleFilters = useCallback(() => setFiltersOpen(prevState => !prevState), []);
    const handleApplyFilters = useCallback(
        customFilters => {
            setFilters(prevState => ({ ...prevState, ...customFilters, page: 1 }));
        },
        [setFilters]
    );

    const handlePageChange = useCallback(
        page => () => setFilters(prevState => ({ ...prevState, page })),
        []
    );

    const onSendSMS = useCallback(
        user => () => {
            setReceiver(user);
            setSmsOpen(true);
        },
        []
    );

    const onCredit = useCallback(payment => {
        setProcessingPayment(payment);
        setCreditOpen(true);
    }, []);

    const handleCreditClose = useCallback(() => {
        setCreditOpen(false);
        setProcessingPayment(null);
    }, []);

    const handlePaymentsExport = useCallback(() => {
        exportPayments(filters);
    }, [filters, exportPayments]);

    useEffect(() => {
        // reset page to 1 when selected keyword is changed
        setFilters(prevState => ({
            ...prevState,
            keyword: debouncedKeyword,
            page: 1,
        }));
    }, [debouncedKeyword]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <Box className={classes.container}>
            <SMSDialog open={smsOpen} handleClose={() => setSmsOpen(false)} receiver={receiver} />
            <CreditDialog
                open={creditOpen}
                onClose={handleCreditClose}
                filters={filters}
                data={processingPayment}
            />
            <Typography variant="h5" color="primary" gutterBottom>
                {title}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="space-between" padding={2}>
                <Box display="flex" alignItems="center" padding={2}>
                    <QuickSearchField
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        handleClear={() => setKeyword('')}
                    />
                    <FiltersButton onClick={handleToggleFilters} />
                </Box>
                {(listStatus === paymentStatus.PENDING_CONFIRMATION ||
                    listStatus === paymentStatus.CONFIRMED) && (
                    <ActionButton
                        variant="outlined"
                        sx={listingActionButtonStyle}
                        disabled={exportLoading}
                        onClick={handlePaymentsExport}
                    >
                        {texts.EXPORTER_LISTE}
                    </ActionButton>
                )}
            </Box>
            <PaymentFilters
                open={filtersOpen}
                applyFilters={handleApplyFilters}
                loading={listLoader}
            />
            <TableContainer>
                <Table style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                        {filteredTableHeads.map(th => {
                            let extraWidth = th.id === 1 ? leftOutWidth : 0;
                            return <col key={th.label} width={`${th.width + extraWidth}%`} />;
                        })}
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            {filteredTableHeads.map(th => (
                                <TableCell key={th.id}>{th.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    {!transfers.length || listLoader ? (
                        <EmptyTableBody loading={listLoader} colSpan={filteredTableHeads.length} />
                    ) : (
                        <TableBody>
                            {transfers.map(t => (
                                <TransferRow
                                    key={t[0].id}
                                    transfer={t}
                                    onSendSMS={onSendSMS}
                                    onCredit={onCredit}
                                    filters={filters}
                                    refetch={refetch}
                                    classes={classes}
                                    hasActions={hasActions}
                                    isServicer={isServicer && !userIsStandardServicer}
                                />
                            ))}
                            <TotalRow
                                hasActions={hasActions && isServicer}
                                classes={classes}
                                total={totalValue}
                                label={texts.TOTAL_TRANSFERS}
                            />
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <CustomTablePagination
                                currentPage={filters.page}
                                lastPage={lastPage}
                                changePage={handlePageChange}
                                columns={filteredTableHeads.length}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    );
};

TransfersTable.propTypes = {
    title: PropTypes.string.isRequired,
    hasActions: PropTypes.bool,
    listStatus: PropTypes.string,
};

export default TransfersTable;
